import { type Clip, ClipType } from '@air/api/types';
import { PlayingCardsFanIcon } from '@air/design-icons';
import {
  GALLERY_CANVAS_IMAGE_TOOL_COUNT,
  getAddToCanvasMenuState,
  pickRandomCanvasImageEditTools,
  SMART_RESIZE_CANVAS_IMAGE_TOOL_ID,
  shuffleWithSeed,
} from '@air/feature-edit-canvas';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import {
  type EditInCanvasSizeSet,
  type PendingCanvasEditRequest,
} from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';

type EditInCanvasAction = PendingCanvasEditRequest['action'];

type EditInCanvasSubItem = {
  action: EditInCanvasAction;
  option: DropdownMenuOption;
};

type GetEditInCanvasSubItemsParams = {
  clips: Clip[];
  /** Offered as "Resize for [set name]" in the image list; the caller decides who is offered a set. */
  offeredSizeSet: EditInCanvasSizeSet | null;
  onSelect: (request: PendingCanvasEditRequest) => void;
};

export type EditInCanvasSubItems = {
  /** The job behind each item, in display order, for `edit_in_canvas_submenu_shown.tool_actions`. */
  actions: EditInCanvasAction[];
  options: DropdownMenuOption[];
};

const MORE_EDITS_IN_CANVAS_SEPARATOR: DropdownMenuOption = { id: 'open-in-canvas-separator', type: 'separator' };

const getMoreEditsInCanvasItem = (onSelect: (request: PendingCanvasEditRequest) => void): DropdownMenuOption => ({
  ...getDropdownMenuOption('open-in-canvas'),
  onSelect: () => onSelect({ action: 'open' }),
  type: 'item',
});

type GetImageEditInCanvasSubItemsParams = {
  offeredSizeSet: EditInCanvasSizeSet | null;
  onSelect: (request: PendingCanvasEditRequest) => void;
  seed: string;
};

const getResizeForSetSubItem = (
  sizeSet: EditInCanvasSizeSet,
  onSelect: (request: PendingCanvasEditRequest) => void,
): EditInCanvasSubItem => {
  const sizeCount = sizeSet.formats.length;

  return {
    action: 'resizeForSet',
    option: {
      description: `${sizeCount} size${sizeCount === 1 ? '' : 's'}`,
      id: 'resize-for-set-in-canvas',
      label: `Resize for ${sizeSet.name}`,
      onSelect: () => onSelect({ action: 'resizeForSet', sizeSet }),
      prefix: <PlayingCardsFanIcon className="size-4" />,
      type: 'item',
    },
  };
};

/**
 * An offered set always takes one of the tool slots, like Animate, and is shuffled in with the rest.
 * "Social variants" leaves the rotation meanwhile: the set item is its one-click version.
 */
const getImageEditInCanvasSubItems = ({
  offeredSizeSet,
  onSelect,
  seed,
}: GetImageEditInCanvasSubItemsParams): EditInCanvasSubItems => {
  const imageTools = pickRandomCanvasImageEditTools(
    seed,
    offeredSizeSet
      ? { count: GALLERY_CANVAS_IMAGE_TOOL_COUNT - 1, excludeIds: [SMART_RESIZE_CANVAS_IMAGE_TOOL_ID] }
      : undefined,
  );
  const imageToolSubItems = imageTools.map((tool): EditInCanvasSubItem => {
    const Icon = tool.icon;

    return {
      action: tool.action,
      option: {
        id: tool.id,
        label: tool.label,
        onSelect: () => onSelect({ action: tool.action }),
        prefix: <Icon className="size-4" />,
        type: 'item',
      },
    };
  });
  const subItems = offeredSizeSet
    ? // Salted so the slot doesn't replay the random sequence that picked the tools.
      shuffleWithSeed(
        [...imageToolSubItems, getResizeForSetSubItem(offeredSizeSet, onSelect)],
        `${seed}:resize-for-set`,
      )
    : imageToolSubItems;

  return {
    actions: [...subItems.map((subItem) => subItem.action), 'open'],
    options: [
      ...subItems.map((subItem) => subItem.option),
      MORE_EDITS_IN_CANVAS_SEPARATOR,
      getMoreEditsInCanvasItem(onSelect),
    ],
  };
};

const getVideoEditInCanvasSubItems = (
  clips: Clip[],
  onSelect: (request: PendingCanvasEditRequest) => void,
): EditInCanvasSubItems => {
  const { canAdd: canConvertToGif, disabledReason: convertToGifDisabledReason } = getAddToCanvasMenuState(clips);
  const videoTools: EditInCanvasSubItem[] = [
    {
      action: 'prompt',
      option: {
        ...getDropdownMenuOption('prompt-in-canvas'),
        onSelect: () => onSelect({ action: 'prompt' }),
        type: 'item',
      },
    },
    {
      action: 'smartResize',
      option: {
        ...getDropdownMenuOption('smart-resize-in-canvas'),
        onSelect: () => onSelect({ action: 'smartResize' }),
        type: 'item',
      },
    },
    {
      // Listed even when disabled: it is still shown.
      action: 'videoConvert',
      option: {
        ...getDropdownMenuOption('convert-to-gif-in-canvas'),
        description: canConvertToGif ? undefined : convertToGifDisabledReason,
        disabled: !canConvertToGif,
        ...(canConvertToGif ? { onSelect: () => onSelect({ action: 'videoConvert' }) } : {}),
        type: 'item',
      },
    },
  ];

  return {
    actions: [...videoTools.map((tool) => tool.action), 'open'],
    options: [
      ...videoTools.map((tool) => tool.option),
      MORE_EDITS_IN_CANVAS_SEPARATOR,
      getMoreEditsInCanvasItem(onSelect),
    ],
  };
};

/**
 * Video-only selections (one clip or many) get video jobs. Mixed image/video groups use the
 * image tool list; those jobs are not armed on video nodes.
 */
export const getEditInCanvasSubItems = ({
  clips,
  offeredSizeSet,
  onSelect,
}: GetEditInCanvasSubItemsParams): EditInCanvasSubItems => {
  const [firstClip] = clips;

  if (!firstClip) {
    return { actions: [], options: [] };
  }

  if (clips.every((clip) => clip.type === ClipType.video)) {
    return getVideoEditInCanvasSubItems(clips, onSelect);
  }

  const seed = clips
    .map((clip) => clip.id)
    .sort((a, b) => a.localeCompare(b))
    .join(':');

  return getImageEditInCanvasSubItems({ offeredSizeSet, onSelect, seed });
};
